import React, { Component } from "react";
import cloneDeep from "lodash/cloneDeep";
import Modal from "@cloudscape-design/components/modal";
import Input from "@cloudscape-design/components/input";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import Select from "@cloudscape-design/components/select";
import SpaceBetween from "@cloudscape-design/components/space-between";
import DatePicker from "@cloudscape-design/components/date-picker";
import Multiselect from "@cloudscape-design/components/multiselect";
import FormField from "@cloudscape-design/components/form-field";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { tournamentsBannersEditModalStore } from "../stores/tournamentsBannersEditModal";
import { MAX_BANNER_BYTES } from "../stores/bannersAddModal";
import { tournamentsBannersStore } from "../stores/tournamentsBanners";
import { gameStore } from "../stores/games";

const MAX_VISIBLE_GAMES = 100;
const LIMIT_OPTIONS = [
	{ value: "0", label: "0 - All players" },
	{ value: "1", label: "1 - VIP players" },
];

const parseGameIds = (value) => {
	if (Array.isArray(value)) {
		return value
			.map((item) => (typeof item === "object" && item !== null ? item.value ?? item.gameID ?? item.id : item))
			.map((id) => parseInt(String(id).trim(), 10))
			.filter((id) => !isNaN(id) && id > 0);
	}

	return String(value || "")
		.split(",")
		.map((id) => parseInt(id.trim(), 10))
		.filter((id) => !isNaN(id) && id > 0);
};

const formatGameLabel = (gameName, gameId) => `${gameName || "Game"} #${gameId}`;

const buildActiveGameOptions = (games = []) =>
	games
		.filter((game) => Number(game?.active) === 1 && game?.gameID)
		.slice()
		.sort((left, right) => {
			const leftName = String(left?.name || "").toLowerCase();
			const rightName = String(right?.name || "").toLowerCase();

			return leftName.localeCompare(rightName, undefined, { numeric: true, sensitivity: "base" }) || Number(left.gameID) - Number(right.gameID);
		})
		.map((game) => ({
			value: game.gameID.toString(),
			label: formatGameLabel(game.name, game.gameID),
		}));

const isValidBannerFileSize = (file) => Boolean(file) && file.size <= MAX_BANNER_BYTES;

const getLimitOption = (value) => (Number(value) > 0 ? LIMIT_OPTIONS[1] : LIMIT_OPTIONS[0]);

class TournamentsBannersEditModal extends Component {
	constructor(props) {
		super(props);

		this.state = {
			loading: false,
			games: [],
			filteredGames: [],
			showDeleteModal: false,
			isDeleting: false,
		};
	}

	componentDidMount() {
		this.syncGameOptions();
	}

	componentDidUpdate(prevProps) {
		if (!prevProps.tournamentsBannersEditModal.show && this.props.tournamentsBannersEditModal.show) {
			this.resetFormFields();
		}

		if (prevProps.game.data !== this.props.game.data) {
			this.syncGameOptions();
		}
	}

	addBanner() {
		this.setState({
			loading: true,
		});

		const body = {
			name: { text: this.props.tournamentsBannersEditModal.name, type: this.props.tournamentsBannersEditModal.type.value },
			oldName: this.props.tournamentsBannersEditModal.oldName,
			badge: this.props.tournamentsBannersEditModal.badge,
			image: this.props.tournamentsBannersEditModal.image,
			limit: Number(this.props.tournamentsBannersEditModal.limit) > 0 ? 1 : 0,
			pool: this.props.tournamentsBannersEditModal.pool,
			gameID: this.props.tournamentsBannersEditModal.gameID ? parseGameIds(this.props.tournamentsBannersEditModal.gameID) : false,
			date: Math.floor(new Date(this.props.tournamentsBannersEditModal.date).getTime() / 1000),
		};
		this.props.Interface.request(
			"set",
			{
				class: "data",
				module: "tournamentBanners",
				method: "edit",
			},
			{ ...body },
		)
			.then((result) => {
				this.setState({
					loading: false,
				});
				this.props.Interface.addNoty("Success", "Tournaments banner was successfully edit");
				this.props.setTournamentsBannersEditModal({ ...this.props.tournamentsBannersEditModal, show: false });
				this.props.Interface.loadTournamentsBannerList();
			})
			.catch((error) => {
				if (typeof error.text === "undefined") error = { text: error.toString() };
				this.props.setTournamentsBanners({ ...this.props.tournamentsBanners, loaded: true, error });

				this.setState({
					loading: false,
				});
			});
	}

	deleteBanner(name) {
		this.setState({ isDeleting: true });
		const data = {
			name,
		}
		this.props.Interface.request("set", {
			class: "data",
			module: "tournamentBanners",
			method: "delete"
		}, { ...data }).then((result) => {
			this.props.Interface.addNoty("Success", "Delete " + name.text  + " was success");
			this.setState({ showDeleteModal: false, isDeleting: false });
			this.props.setTournamentsBannersEditModal({ ...this.props.tournamentsBannersEditModal, show: false });
			this.props.Interface.loadTournamentsBannerList();
		}).catch((error) => {
			if (typeof error.text === "undefined") error = { text: error.toString() };
			this.props.Interface.addNoty("Error", error.text);
			this.props.setTournamentsBanners({ ...this.props.tournamentsBanners, loaded: true, error });
			this.setState({ isDeleting: false });
		})
	}

	openDeleteModal = () => {
		if (!this.props.tournamentsBannersEditModal.oldName) return;
		this.setState({ showDeleteModal: true });
	};

	closeDeleteModal = () => {
		if (this.state.isDeleting) return;
		this.setState({ showDeleteModal: false });
	};

	confirmDelete = () => {
		const { oldName } = this.props.tournamentsBannersEditModal;
		if (!oldName) return;
		this.deleteBanner(oldName);
	};

	closeEditModal = () => {
		if (this.state.isDeleting) return;
		this.setState({ showDeleteModal: false });
		this.props.setTournamentsBannersEditModal({ ...this.props.tournamentsBannersEditModal, show: false });
	};

	resetFormFields = () => {
		this.setState({
			filteredGames: this.state.games.slice(0, MAX_VISIBLE_GAMES),
		});

		this.props.setTournamentsBannersEditModal({
			...this.props.tournamentsBannersEditModal,
			type: "",
			name: "",
			badge: "",
			gameID: "",
			pool: "",
			limit: "0",
			date: "",
			image: "",
			error: {
				text: "",
				code: 0,
				timestamp: 0,
			},
		});
	};

	updateParam = (key, value) => {
		const updatedState = cloneDeep(this.props.tournamentsBannersEditModal);
		updatedState[key] = value;
		this.props.setTournamentsBannersEditModal(updatedState);
	};

	syncGameOptions = () => {
		const games = buildActiveGameOptions(this.props.game?.data || []);

		this.setState({
			games,
			filteredGames: games.slice(0, MAX_VISIBLE_GAMES),
		});
	};

	getSelectedGameOptions = () => {
		const selectedIds = parseGameIds(this.props.tournamentsBannersEditModal.gameID);

		return selectedIds.map((id) => {
			const optionFromActiveGames = this.state.games.find((option) => option.value === id.toString());

			if (optionFromActiveGames) return optionFromActiveGames;

			const existingGame = (this.props.game?.data || []).find((game) => Number(game?.gameID) === id);

			return {
				value: id.toString(),
				label: formatGameLabel(existingGame?.name || "Game", id),
			};
		});
	};

	handleGameSearch = (filteringText) => {
		const normalizedFilter = String(filteringText || "")
			.trim()
			.toLowerCase();
		const filteredGames = normalizedFilter.length === 0 ? this.state.games : this.state.games.filter((game) => game.label.toLowerCase().includes(normalizedFilter) || game.value.includes(normalizedFilter));

		this.setState({
			filteredGames: filteredGames.slice(0, MAX_VISIBLE_GAMES),
		});
	};

	handleGameChange = ({ detail }) => {
		this.updateParam("gameID", detail.selectedOptions.map((option) => option.value).join(", "));
	};

	render() {
		const { tournamentsBannersEditModal, setTournamentsBannersEditModal } = this.props;
		const selectedGameOptions = this.getSelectedGameOptions();
		const { showDeleteModal, isDeleting } = this.state;
		const deleteName = tournamentsBannersEditModal.oldName;

		return (
			<>
				<Modal
					visible={tournamentsBannersEditModal.show}
					onDismiss={this.closeEditModal}
					header="Edit tournament banner"
					children={
						<SpaceBetween direction="vertical" size="m">
							<Box variant="span">Name Type</Box>
							<Select filteringType="auto" placeholder="Select name type" selectedOption={tournamentsBannersEditModal.type} onChange={(option) => this.updateParam("type", option.detail.selectedOption)} options={tournamentsBannersEditModal.currentOption.type} />
							<Box variant="span">Name</Box>
							<Input
								name="Title"
								autoComplete="off"
								inputMode="text"
								type="text"
								disableBrowserAutocorrect={true}
								placeholder="Name"
								value={tournamentsBannersEditModal.name}
								onChange={(event) =>
									setTournamentsBannersEditModal({
										...tournamentsBannersEditModal,
										name: event.detail.value,
									})
								}
							/>
							<Box variant="span">Badge</Box>
							<Input
								name="Badge"
								autoComplete="off"
								inputMode="text"
								type="text"
								disableBrowserAutocorrect={true}
								placeholder="Badge"
								value={tournamentsBannersEditModal.badge}
								onChange={(event) =>
									setTournamentsBannersEditModal({
										...tournamentsBannersEditModal,
										badge: event.detail.value,
									})
								}
							/>
							<Box variant="span">Image</Box>
							<input
								type="file"
								accept="image/jpg"
								onChange={(event) => {
									const file = event.target.files[0];
									if (file) {
										if (!isValidBannerFileSize(file)) {
											this.props.Interface.addNoty("Error", `Image size must not exceed ${MAX_BANNER_BYTES / 1024 / 1024} MB`);
											event.target.value = "";

											return;
										}

										const reader = new FileReader();
										reader.onload = (event) => {
											const result = event.target.result;
											this.updateParam("image", result);
										};
										reader.readAsDataURL(file);
									} else {
										this.props.Interface.addNoty("Error", "Please, select a valid image file");
									}
								}}
							/>
							<FormField label="Games" description="Select up to 100 visible active games; search by game name or ID" stretch>
								<Multiselect filteringType="manual" onLoadItems={({ detail }) => this.handleGameSearch(detail.filteringText)} selectedOptions={!this.props.game.loaded ? [] : selectedGameOptions} onChange={this.handleGameChange} options={this.state.filteredGames} statusType={!this.props.game.loaded ? "loading" : "finished"} disabled={this.state.games.length === 0} placeholder={!this.props.game.loaded ? "Loading games..." : "Select games"} />
							</FormField>
							<Box variant="span">Pool</Box>
							<Input
								name="Title"
								autoComplete="off"
								inputMode="number"
								type="number"
								disableBrowserAutocorrect={true}
								placeholder="Pool"
								value={tournamentsBannersEditModal.pool}
								onChange={(event) =>
									setTournamentsBannersEditModal({
										...tournamentsBannersEditModal,
										pool: event.detail.value,
									})
								}
							/>
							<Box variant="span">Limit</Box>
							<Select
								placeholder="Limit"
								selectedOption={getLimitOption(tournamentsBannersEditModal.limit)}
								onChange={(option) => this.updateParam("limit", option.detail.selectedOption.value)}
								options={LIMIT_OPTIONS}
							/>
							<Box variant="span">Date</Box>
							<DatePicker
								onChange={({ detail }) => {
									setTournamentsBannersEditModal({
										...tournamentsBannersEditModal,
										date: detail.value,
									});
								}}
								value={tournamentsBannersEditModal.date}
								openCalendarAriaLabel={(selectedDate) => "Choose certificate expiry date" + (selectedDate ? `, selected date is ${selectedDate}` : "")}
								placeholder="YYYY/MM/DD"
								nextMonthAriaLabel={""}
								previousMonthAriaLabel={""}
								todayAriaLabel={""}
							/>
						</SpaceBetween>
					}
					footer={
						<Box float="right">
							<SpaceBetween direction="horizontal" size="m">
								{tournamentsBannersEditModal.oldName ? (
									<Button className={[this.props.styles["btnUID"], this.props.styles["blue"]].join(" ")} variant="normal" onClick={this.openDeleteModal} disabled={this.state.loading || isDeleting} children="Delete" />
								) : (
									<></>
								)}
								<Button variant="normal" onClick={this.closeEditModal} children="Close" />
								<Button variant="primary" disabled={this.state.loading || isDeleting} onClick={() => this.addBanner()} children="Ok" />
							</SpaceBetween>
						</Box>
					}
				/>

				<Modal
					visible={showDeleteModal}
					onDismiss={this.closeDeleteModal}
					header="Confirm Deletion"
					footer={
						<Box float="right">
							<SpaceBetween direction="horizontal" size="xs">
								<Button variant="link" onClick={this.closeDeleteModal} disabled={isDeleting} children="Close" />
								<Button variant="primary" onClick={this.confirmDelete} loading={isDeleting} disabled={isDeleting} children="Apply" />
							</SpaceBetween>
						</Box>
					}
				>
					<SpaceBetween direction="vertical" size="xs">
						<Box variant="p">Are you sure you want to delete this tournament banner? This action will delete the saved banner.</Box>
						<Box variant="p">Name: {deleteName ? deleteName.text : ""}</Box>
						<Box variant="p">Type: {deleteName ? deleteName.type : ""}</Box>
					</SpaceBetween>
				</Modal>
			</>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const tournamentsBannersEditModal = useRecoilValue(tournamentsBannersEditModalStore);
		const setTournamentsBannersEditModal = useSetRecoilState(tournamentsBannersEditModalStore);
		const tournamentsBanners = useRecoilValue(tournamentsBannersStore);
		const setTournamentsBanners = useSetRecoilState(tournamentsBannersStore);
		const game = useRecoilValue(gameStore);
		return <Component {...props} tournamentsBanners={tournamentsBanners} setTournamentsBanners={setTournamentsBanners} setTournamentsBannersEditModal={setTournamentsBannersEditModal} tournamentsBannersEditModal={tournamentsBannersEditModal} game={game} />;
	};
}

export default withRecoil(TournamentsBannersEditModal);
